import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { 
  BsBox, 
  BsClock, 
  BsCheckCircle, 
  BsTruck, 
  BsStar,
  BsEye,
  BsCalendar,
  BsCurrencyDollar,
  BsTag,
  BsChevronDown,
  BsChevronUp,
  BsArrowRight,
  BsCalendarEvent,
  BsCreditCard,
  BsDownload
} from "react-icons/bs";
import { BiPackage, BiMap, BiTime, BiX } from "react-icons/bi";
import Meta from "../components/Meta";
import Container from "../components/Container";
import { getSingleOrder } from "../features/user/userSlice";
import { base_url } from "../utils/axiosConfig";

const TrackOrder = () => {
  const { orderId } = useParams();
  const dispatch = useDispatch();
  const orderState = useSelector((state) => state?.auth?.singleOrder?.order);
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (orderId) {
      dispatch(getSingleOrder(orderId));
    }
  }, [orderId, dispatch]);

  useEffect(() => {
    if (orderState && orderState.delhivery && orderState.delhivery.waybillNumber) {
      fetchTrackingInfo(orderState.delhivery.waybillNumber);
    }
  }, [orderState]);

  const fetchTrackingInfo = async (waybillNumber) => {
    setLoading(true);
    setError(null);
    
    try {
      const customer = localStorage.getItem("customer");
      const token = customer ? JSON.parse(customer).token : null;
      
      const response = await fetch(`${base_url}user/delhivery/track-shipment/${waybillNumber}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTrackingInfo(data.trackingInfo);
      } else {
        setError("Failed to fetch tracking information");
      }
    } catch (err) {
      console.error("Error fetching tracking info:", err);
      setError("Failed to fetch tracking information");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'out for delivery':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
      case 'dispatched':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
      case 'rto initiated':
      case 'rto delivered':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return <BsCheckCircle className="text-green-600" />;
      case 'out for delivery':
        return <BiMap className="text-blue-600" />;
      case 'shipped':
      case 'dispatched':
        return <BiPackage className="text-blue-600" />;
      case 'cancelled':
      case 'rto initiated':
      case 'rto delivered':
        return <BiX className="text-red-600" />;
      default:
        return <BiTime className="text-gray-600" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Track Order - VEDA Merchandise",
    "description": "Track your order status and delivery information in real-time.",
    "url": `https://vedamerchandise.com/track-order/${orderId}`,
    "publisher": {
      "@type": "Organization",
      "name": "VEDA Merchandise",
      "logo": {
        "@type": "ImageObject",
        "url": "https://vedamerchandise.com/logo.svg"
      }
    }
  };

  return (
    <>
      <Meta 
        title="Track Order - VEDA Merchandise"
        description="Track your order status and delivery information in real-time."
        keywords="track order, order status, delivery tracking, VEDA order tracking"
        author="VEDA Merchandise"
        ogTitle="Track Order - VEDA Merchandise"
        ogDescription="Track your order status and delivery information in real-time."
        ogImage="https://vedamerchandise.com/tracking-preview.jpg"
        ogUrl={`https://vedamerchandise.com/track-order/${orderId}`}
        ogType="website"
        twitterCard="summary_large_image"
        twitterTitle="Track Order - VEDA Merchandise"
        twitterDescription="Track your order status and delivery information in real-time."
        twitterImage="https://vedamerchandise.com/tracking-preview.jpg"
        robots="noindex, nofollow"
        canonicalUrl={`https://vedamerchandise.com/track-order/${orderId}`}
        schemaMarkup={schemaMarkup}
      />
      
      <Container class1="track-order-wrapper py-5">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-bold text-gray-900">Track Your Order</h1>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-[#e91e63] text-white rounded-lg hover:bg-[#d81557] transition-colors"
            >
              Refresh Tracking
            </button>
          </div>
          
          {loading && (
            <div className="flex justify-center items-center py-10">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#e91e63]"></div>
            </div>
          )}
          
          {error && (
            <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              <strong>Error:</strong> {error}
            </div>
          )}
          
          {orderState && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Order Summary</h2>
                  <div className="space-y-4">
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Order ID</p>
                      <p className="font-medium">{orderState?.customOrderId || `#${orderState._id?.slice(-8).toUpperCase()}`}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Order Date</p>
                      <p className="font-medium">{formatDate(orderState.createdAt)}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Total Amount</p>
                      <p className="font-medium">₹{orderState.totalPriceAfterDiscount?.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Status</p>
                      <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${getStatusColor(orderState.orderStatus)}`}>
                        {getStatusIcon(orderState.orderStatus)}
                        <span>{orderState.orderStatus}</span>
                      </span>
                    </div>
                  </div>
                </div>
                
                {/* Customer Information */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Customer Information</h2>
                  <div className="space-y-4">
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Customer Name</p>
                      <p className="font-medium">
                        {orderState.user?.firstname} {orderState.user?.lastname}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Email</p>
                      <p className="font-medium">{orderState.user?.email}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Mobile</p>
                      <p className="font-medium">{orderState.user?.mobile}</p>
                    </div>
                  </div>
                </div>
                
                {/* Shipping Address */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">Shipping Address</h2>
                  <div className="space-y-4">
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Recipient</p>
                      <p className="font-medium">
                        {orderState.shippingInfo?.firstname} {orderState.shippingInfo?.lastname}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Address</p>
                      <p className="font-medium">{orderState.shippingInfo?.address}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm mb-1">City</p>
                      <p className="font-medium">{orderState.shippingInfo?.city}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm mb-1">State</p>
                      <p className="font-medium">{orderState.shippingInfo?.state}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Pincode</p>
                      <p className="font-medium">{orderState.shippingInfo?.pincode}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm mb-1">Country</p>
                      <p className="font-medium">{orderState.shippingInfo?.country || "India"}</p>
                    </div>
                  </div>
                </div>
                
                {/* Delhivery Information */}
                {orderState.delhivery?.waybillNumber && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">Delhivery Information</h2>
                    <div className="space-y-4">
                      <div>
                        <p className="text-gray-500 text-sm mb-1">AWB Number</p>
                        <p className="font-medium">{orderState.delhivery.waybillNumber}</p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm mb-1">Shipment Status</p>
                        <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${getStatusColor(orderState.delhivery.status)}`}>
                          {getStatusIcon(orderState.delhivery.status)}
                          <span>{orderState.delhivery.status || "Pending"}</span>
                        </span>
                      </div>
                      <div>
                        <p className="text-gray-500 text-sm mb-1">Tracking Link</p>
                        <a 
                          href={orderState.delhivery.trackingUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-[#e91e63] hover:underline font-medium"
                        >
                          Track on Delhivery
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Tracking Information */}
              <div className="lg:col-span-2">
                {trackingInfo && trackingInfo.ShipmentData && trackingInfo.ShipmentData.length > 0 ? (
                  <div className="space-y-6">
                    {/* Timeline */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                      <h2 className="text-xl font-bold text-gray-900 mb-4">Tracking Timeline</h2>
                      <div className="relative">
                        {/* Vertical line */}
                        <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                        
                        <div className="space-y-6">
                          {trackingInfo.ShipmentData[0].ScanDetails.map((scan, index) => (
                            <div key={index} className="relative flex items-start">
                              {/* Icon */}
                              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white border-2 border-[#e91e63] flex items-center justify-center z-10">
                                {getStatusIcon(scan.Status)}
                              </div>
                              
                              {/* Content */}
                              <div className="ml-4 pb-6">
                                <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold ${getStatusColor(scan.Status)}`}>
                                  <span>{scan.Status}</span>
                                </div>
                                <p className="text-gray-500 text-sm mt-2">
                                  {new Date(scan.ScanDateTime).toLocaleString()}
                                </p>
                                <p className="text-gray-600 text-sm">{scan.ScannedLocation}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {/* Order Items */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                      <h2 className="text-xl font-bold text-gray-900 mb-4">Order Items</h2>
                      <div className="space-y-4">
                        {orderState.orderItems?.map((item, index) => (
                          <div key={index} className="flex flex-col sm:flex-row gap-4 p-4 border border-gray-200 rounded-lg">
                            {/* Product Image */}
                            <div className="flex-shrink-0">
                              <img
                                src={item.product?.images?.[0]?.url || "https://via.placeholder.com/100"}
                                alt={item.product?.title}
                                className="w-full sm:w-24 h-24 object-cover rounded-lg"
                              />
                            </div>
                            
                            {/* Product Details */}
                            <div className="flex-grow">
                              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                                <div className="flex-grow">
                                  <h3 className="font-bold text-gray-900">{item.product?.title}</h3>
                                  <p className="text-sm text-gray-600 mb-2">
                                    Brand: {item.product?.brand}
                                  </p>
                                  <p className="text-sm text-gray-500">
                                    Product ID: {item.product?.customProductId || `#${item.product?._id?.slice(-8).toUpperCase()}`}
                                  </p>
                                </div>
                                <div className="flex sm:flex-col items-center sm:items-end gap-4 sm:gap-2">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm text-gray-600">Qty:</span>
                                    <span className="px-3 py-1 bg-gray-100 rounded-full text-sm font-bold">
                                      {item.quantity}
                                    </span>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-sm text-gray-500">₹{item.price?.toLocaleString()} each</div>
                                    <div className="font-bold text-lg text-[#e91e63]">
                                      ₹{(item.price * item.quantity)?.toLocaleString()}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-6">
                      <BiPackage className="text-3xl text-gray-400" />
                    </div>
                    <h3 className="text-xl font-medium text-gray-900 mb-2">Tracking Information Not Available</h3>
                    <p className="text-gray-500">
                      Tracking information will be available once your order is processed and shipped.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Container>
    </>
  );
};

export default TrackOrder;